/**
 * Warehouse Transfers Connection Fix
 * 
 * This script fixes connection issues between the client application
 * and the warehouse transfers API endpoints.
 */

import apiService from './apiService';
import { API_BASE_URL } from '../config';

// Token from the .env file
const SAVED_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjp7ImlkIjoiNjdkMGE2NjU2YjQ1NzMxY2RmNzg4OTMzIiwidXNlcm5hbWUiOiJhZG1pbiIsInJvbGUiOiJhZG1pbiJ9LCJpYXQiOjE3NDMxMTI5OTAsImV4cCI6MTc0MzE1NjE5MH0.Ccd170qirWb6EEi-um90FOd15PvofvQ3n6GTQ_Y_yWU';

/**
 * احصل على توكن المصادقة من localStorage بدون إعادة كتابته
 */
export function getAuthToken() {
  // استخدام توكن المستخدم الحالي بدلاً من الكتابة فوقه
  const currentToken = localStorage.getItem('token');
  if (currentToken) {
    console.log('Using current user authentication token');
    return currentToken;
  }
  // استخدام التوكن المحفوظ فقط إذا لم يكن هناك توكن حالي
  if (SAVED_TOKEN && !currentToken) {
    console.log('No token found, using saved token as fallback');
    localStorage.setItem('token', SAVED_TOKEN);
    return SAVED_TOKEN;
  }
  return null;
}

/**
 * Direct API access for warehouse transfers
 * This bypasses the normal apiService error handling for diagnostic purposes
 */
export async function directWarehouseTransfersAccess(limit = 10, queryParams = '') {
  try {
    // استخدام توكن المستخدم الحالي
    const token = getAuthToken();
    if (!token) {
      throw new Error('لم يتم العثور على توكن صالح للمصادقة');
    }
    
    // استخدام التوكن الذي حصلنا عليه
    
    // Build URL with query parameters
    const url = queryParams
      ? `${API_BASE_URL}/warehouse-transfers?${queryParams}`
      : `${API_BASE_URL}/warehouse-transfers?limit=${limit}`;
    
    // Make direct API call
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Server responded with status: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      transfers: data.transfers || [],
      total: data.total || 0
    };
  } catch (error) {
    console.error('Direct warehouse transfers access failed:', error);
    return {
      success: false,
      message: error.message,
      error
    };
  }
}

/**
 * Modified version of apiService get method with retry mechanism
 * This is specifically designed for warehouse transfers API issues
 */
export async function getWarehouseTransfersWithRetry(maxRetries = 3, delayMs = 1000, queryParams = '') {
  // تأكد من وجود توكن صالح
  const token = getAuthToken();
  if (!token) {
    return {
      success: false,
      message: 'لم يتم العثور على توكن صالح للمصادقة'
    };
  }
  
  let lastError = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Build endpoint with query parameters
      const endpoint = queryParams
        ? `/warehouse-transfers?${queryParams}`
        : '/warehouse-transfers';
      
      // Try normal API service first
      const response = await apiService.get(endpoint, {
        timeout: 10000 // 10 second timeout
      });
      
      return response;
    } catch (error) {
      console.log(`Retry attempt ${attempt + 1}/${maxRetries} failed:`, error.message);
      lastError = error;
      
      // Special handling for 404 errors (might be an API path issue)
      if (error.response && error.response.status === 404) {
        // Try direct access as a fallback
        console.log('Trying direct access as fallback...');
        const directResult = await directWarehouseTransfersAccess(10, queryParams);
        
        if (directResult.success) {
          return directResult;
        }
      }
      
      // Wait before retrying
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }
  
  // If we've reached here, all attempts failed
  return {
    success: false,
    message: 'فشل الاتصال بخدمة تحويلات المستودعات بعد عدة محاولات',
    error: lastError
  };
}

/**
 * Fix warehouse API client configuration
 */
export function checkAndFixApiConfig() {
  // Check if API_BASE_URL is correctly set
  if (!API_BASE_URL.includes('localhost:5050')) {
    console.warn(`API URL may be incorrect: ${API_BASE_URL}. Using direct address instead.`);
    // We use direct address as a fallback
    return {
      success: false,
      message: 'تم اكتشاف خطأ في إعدادات عنوان API. استخدام العنوان المباشر.'
    };
  }
  return { success: true };
}

// Export an object with all the fix functions
const warehouseTransferFix = {
  getAuthToken,
  directWarehouseTransfersAccess,
  getWarehouseTransfersWithRetry,
  checkAndFixApiConfig
};

export default warehouseTransferFix;